import { useCallback, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Copy, Inbox, X } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { useMe } from "@/context/MeContext";
import { useApi, errMsg } from "@/lib/api";
import { fmtDate, isStaff, money } from "@/lib/format";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUS = {
  queued: ["Received", "text-zinc-400"], processing: ["Reading…", "text-zinc-300"], added: ["Added", "text-emerald-400"],
  unmatched: ["Needs a trip", "text-amber-300"], failed: ["Couldn't read", "text-rose-300"], ignored: ["Ignored", "text-zinc-500"],
};
const WEBHOOK_URL = `${process.env.REACT_APP_BACKEND_URL}/api/webhooks/resend-inbound`;

function Setup({ address, configured }) {
  const copy = (v, label) => navigator.clipboard.writeText(v).then(() => toast.success(`${label} copied`));
  if (configured && address) {
    return (
      <div className="lux-card p-5 flex flex-wrap items-center gap-3" data-testid="quote-inbox-address-card">
        <Inbox size={18} className="text-[#5B7FD1]" />
        <div className="flex-1 min-w-0">
          <p className="text-[11px] uppercase tracking-wider text-zinc-500">Forward operator quotes to</p>
          <p className="text-white font-mono-data break-all" data-testid="quote-inbox-address">{address}</p>
        </div>
        <button className="frame-btn" onClick={() => copy(address, "Address")} data-testid="quote-inbox-copy-btn"><Copy size={13} /> Copy</button>
      </div>
    );
  }
  return (
    <div className="lux-card p-5 text-sm text-zinc-400 grid gap-2" data-testid="quote-inbox-setup">
      <p className="text-white">{configured ? "Add the receiving address to finish setup." : "The Quote Inbox isn't connected yet."}</p>
      <p>In Resend, open Emails → Receiving to get your receiving address, then add a webhook for the "email.received" event pointing to:</p>
      <button className="font-mono-data text-xs text-[#5B7FD1] break-all text-left" onClick={() => copy(WEBHOOK_URL, "Webhook URL")} data-testid="quote-inbox-webhook-url">{WEBHOOK_URL}</button>
    </div>
  );
}

function QuoteLines({ quotes = [] }) {
  return (
    <ul className="text-xs text-zinc-400 grid gap-0.5">
      {quotes.map((q, i) => <li key={i}>{[q.operator_name, q.aircraft_type, q.tail_number].filter(Boolean).join(" · ")}{q.price ? ` — ${money(q.price, q.currency)}` : ""}</li>)}
    </ul>
  );
}

function PickTrip({ item, trips, onAssign }) {
  const [tid, setTid] = useState(item.candidates?.[0]?.id || "");
  const pool = [...(item.candidates || []), ...trips.filter((t) => !(item.candidates || []).some((c) => c.id === t.id))];
  return (
    <div className="flex flex-wrap gap-2 items-center">
      <Select value={tid} onValueChange={setTid}>
        <SelectTrigger className="w-full sm:w-96 bg-[#0B0D12] border-white/10 h-10" data-testid={`quote-inbox-trip-select-${item.id}`}><SelectValue placeholder="Pick the trip request" /></SelectTrigger>
        <SelectContent>{pool.map((t) => <SelectItem key={t.id} value={t.id}>{t.ref}{t.score ? ` · ${t.score}% match` : ""}</SelectItem>)}</SelectContent>
      </Select>
      <button className="frame-btn gold" disabled={!tid} onClick={() => onAssign(item, tid)} data-testid={`quote-inbox-assign-btn-${item.id}`}>Add options</button>
    </div>
  );
}

function Item({ item, trips, onAssign, onDismiss }) {
  const [label, cls] = STATUS[item.status] || [item.status, "text-zinc-400"];
  return (
    <li className="lux-card p-4 sm:p-5 grid gap-2" data-testid={`quote-inbox-item-${item.id}`}>
      <div className="flex flex-wrap items-start gap-x-3 gap-y-1">
        <span className={`text-[11px] uppercase tracking-wider ${cls}`} data-testid={`quote-inbox-status-${item.id}`}>{label}</span>
        <span className="text-sm text-white flex-1 min-w-[12rem]">{item.subject || "(no subject)"}</span>
        <span className="text-[11px] text-zinc-500">{fmtDate(item.created_at)}</span>
        {item.status !== "added" && <button className="text-zinc-500 hover:text-white" title="Dismiss" onClick={() => onDismiss(item)} data-testid={`quote-inbox-dismiss-${item.id}`}><X size={14} /></button>}
      </div>
      <p className="text-[11px] text-zinc-500">{item.from}</p>
      <QuoteLines quotes={item.extracted?.quotes} />
      {item.detail && <p className="text-xs text-amber-300/90">{item.detail}</p>}
      {item.status === "added" && (
        <p className="text-xs text-emerald-400">Added {item.added?.length || 0} to <Link className="underline" to={`/trips/${item.trip_id}`}>{item.ref}</Link>
          {item.skipped?.length ? <span className="text-amber-300"> · skipped {item.skipped.map((s) => `${s.label} (${s.reason})`).join(", ")}</span> : null}</p>
      )}
      {item.status === "unmatched" && item.extracted?.quotes?.length > 0 && <PickTrip item={item} trips={trips} onAssign={onAssign} />}
    </li>
  );
}

export default function QuoteInbox() {
  const { me } = useMe();
  const api = useApi();
  const [data, setData] = useState(null);
  const [trips, setTrips] = useState([]);
  const load = useCallback(async () => {
    try { setData((await api.get("/quote-inbox")).data); } catch (e) { toast.error(errMsg(e)); }
  }, [api]);
  useEffect(() => {
    load();
    api.get("/trips", { params: { status: "sales" } }).then((r) => setTrips(r.data.trips || [])).catch(() => {});
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [api, load]);
  if (!isStaff(me)) return <Navigate to="/" replace />;
  const assign = async (item, tripId) => {
    try {
      const { data: r } = await api.post(`/quote-inbox/${item.id}/assign`, { trip_id: tripId });
      toast.success(`Added ${r.added.length} option${r.added.length !== 1 ? "s" : ""} to ${r.ref}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const dismiss = async (item) => {
    try { await api.post(`/quote-inbox/${item.id}/dismiss`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="quote-inbox-page">
      <PageHero testId="quote-inbox-hero">
        <p className="eyebrow mb-3">Flight department</p>
        <h1 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-white mb-3">Quote Inbox</h1>
        <p className="text-zinc-300 text-sm max-w-xl">Forward operator quote emails to the address below. Each quote is read and added to the matching trip request. Anything it can't match waits here for you to pick the trip.</p>
      </PageHero>
      <div className="grid gap-6 pb-10">
        {data && <Setup address={data.address} configured={data.configured} />}
        <ul className="grid gap-3" data-testid="quote-inbox-list">
          {data?.items.map((i) => <Item key={i.id} item={i} trips={trips} onAssign={assign} onDismiss={dismiss} />)}
          {data && !data.items.length && <li className="lux-card px-6 py-8 text-center text-sm text-zinc-500" data-testid="quote-inbox-empty">No forwarded quotes yet.</li>}
        </ul>
      </div>
    </div>
  );
}
